import { useEffect, useState } from 'react';
import { Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

/** この端末で最後にログインできたテナントコード（課題 #101。どの組織でも centric が入っていたのをやめる） */
const LAST_TENANT_KEY = 'ceeu.lastTenantCode';

/**
 * テナントコードの初期値。URL の ?tenant= があればそれを、無ければこの端末で最後にログインできたコードを入れる。
 * どちらも無ければ空欄（以前は全組織で「centric」が入っており、他の組織の方が誤って入力するおそれがあった）
 */
function initialTenantCode(search: string): string {
  try {
    const fromUrl = new URLSearchParams(search).get('tenant');
    if (fromUrl && fromUrl.trim()) return fromUrl.trim();
  } catch {
    /* ignore */
  }
  try {
    return window.localStorage.getItem(LAST_TENANT_KEY) ?? '';
  } catch {
    return '';
  }
}

export default function Login() {
  const { t } = useTranslation();
  const { login, user, accessToken, isReady } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionExpired = (location.state as { sessionExpired?: boolean } | null)?.sessionExpired;

  // ブラウザ戻る/進むで location.state.sessionExpired が再復活してバナーが何度も出る現象を防ぐため、
  // バナー表示後に history state を空でリプレイスする（2026-05-31 マルチタブ UX 監査での指摘）。
  useEffect(() => {
    if (sessionExpired && typeof window !== 'undefined') {
      try {
        window.history.replaceState({}, '', window.location.href);
      } catch {
        /* ignore */
      }
    }
  }, [sessionExpired]);
  const [tenantCode, setTenantCode] = useState(() => initialTenantCode(location.search));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isReady) return <div className="loading-screen">{t('common.loading')}</div>;
  if (accessToken && user) {
    const to = user.role === 'employee' ? '/dashboard' : user.role === 'admin' ? '/admin' : '/healthcare';
    return <Navigate to={to} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const u = await login(tenantCode.trim(), email, password);
      try {
        window.localStorage.setItem(LAST_TENANT_KEY, tenantCode.trim());
      } catch {
        /* 保存できなくてもログインは続ける */
      }
      const to = u.role === 'admin' ? '/admin' : u.role === 'healthcare' ? '/healthcare' : '/dashboard';
      navigate(to);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <h1>{t('app.name')}</h1>
        <p className="login-desc">{t('app.description')}</p>
        {sessionExpired && (
          <p className="login-session-expired" role="status">
            {t('auth.sessionExpired')}
          </p>
        )}
        <form onSubmit={handleSubmit} className="login-form">
          <label>
            {t('auth.tenantCode')}
            <input
              type="text"
              value={tenantCode}
              onChange={(e) => setTenantCode(e.target.value)}
              required
              autoComplete="organization"
              placeholder={t('auth.tenantCodePlaceholder')}
            />
          </label>
          <label>
            {t('auth.email')}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </label>
          <label>
            {t('auth.password')}
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </label>
          {error && <p className="login-error">{error}</p>}
          <button type="submit" disabled={loading}>
            {loading ? t('common.loading') : t('common.login')}
          </button>
        </form>
        {/* ログインはタブごとに保存している（課題 No.23）。新しいタブで開いて戻されたときに、理由が分かるよう先に書いておく（#166） */}
        <p className="login-per-tab-note">{t('auth.perTabNote')}</p>
        <p className="login-accept-invite-link">
          <Link to="/accept-invite">{t('auth.acceptInviteLink')}</Link>
        </p>
        <p className="login-forgot-password">{t('auth.forgotPassword')}</p>
      </div>
    </div>
  );
}
